import { CSSProperties } from "react";
import { SocialPlatform } from "@digital-twins/shared-types";

import { getCategoricalChartPalette } from "@/lib/charts/chartjs";

/**
 * Channel colors for the Topic charts: each platform's brand color (owner Q4),
 * so a line or bar reads instantly next to its logo. The only file in the
 * social charts allowed raw hex.
 *
 * TikTok's brand black disappears on the dark surface, so dark mode uses its
 * brand cyan. Every color keeps at least 3:1 against the surfaces it is drawn
 * on (`SOCIAL_CHART_SURFACES`), which the colors test enforces.
 */
export interface SocialPlatformColor {
  light: string;
  dark: string;
}

export const SOCIAL_PLATFORM_COLORS: Record<SocialPlatform, SocialPlatformColor> = {
  reddit: { light: "#FF4500", dark: "#FF4500" },
  youtube: { light: "#FF0000", dark: "#FF0000" },
  tiktok: { light: "#000000", dark: "#25F4EE" },
  instagram: { light: "#E4405F", dark: "#E4405F" },
};

/**
 * The surfaces a chart sits on, mirrored from `globals.css` for the contrast
 * check: `--background` and `--card`, light (`:root`) and dark (`.dark`).
 */
export const SOCIAL_CHART_SURFACES: Record<"light" | "dark", Array<string>> = {
  light: ["#FFFFFF", "#F1F1F1"],
  dark: ["#081919", "#10231F"],
};

/**
 * Both theme colors as CSS variables. Pair with `SOCIAL_PLATFORM_TEXT_CLASS`,
 * which picks the variable for the active theme. Only platform marks take a
 * platform color; chart fills use the theme tokens.
 */
export function socialPlatformColorStyle(platform: SocialPlatform): CSSProperties {
  const color = SOCIAL_PLATFORM_COLORS[platform];
  return {
    "--social-platform-light": color.light,
    "--social-platform-dark": color.dark,
  } as CSSProperties;
}

export const SOCIAL_PLATFORM_TEXT_CLASS =
  "text-[var(--social-platform-light)] dark:text-[var(--social-platform-dark)]";

/** Color of a brand id missing from the Topic's brand list: neutral, never a palette slot. */
export const SOCIAL_BRAND_FALLBACK_COLOR = "hsl(var(--foreground) / 0.6)";

/**
 * One color per brand from the app chart palette (theme and org palette
 * aware), by the brand's position in the Topic's brand list, so a brand keeps
 * its color whatever the sort, measure or filters. Wraps after the palette.
 */
export function socialBrandColors(brands: Array<{ id: string }>): Map<string, string> {
  const palette = getCategoricalChartPalette();
  return new Map(brands.map((brand, index) => [brand.id, palette[index % palette.length]]));
}

function channelLuminance(hex: string): number {
  const value = hex.replace("#", "");
  const channels = [0, 2, 4].map((offset) => {
    const channel = parseInt(value.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** WCAG 2 contrast ratio of two `#rrggbb` colors, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [lighter, darker] = [channelLuminance(a), channelLuminance(b)].sort(
    (x, y) => y - x,
  );
  return (lighter + 0.05) / (darker + 0.05);
}
